/**
 * author: arczhang
 * page: 离线中心
 * description: 线索公海池导入线索后不再立即查看结果，而是通过顶部导航的菜单进入到这个页面查看导入任务是否完成
 * tapd: ID865724595
 */
import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  OverlayTrigger,
  PopOver,
  Table,
  Column,
  PageBar,
} from '@tencent/spaui';
import get from 'lodash/get';

import { judgeIsFromTMC } from '../../../utils'
import { RPCFactory } from '../../../utils/RequestUtil';
import { withNetCall } from '../../common/call/withNetCall';

const STATUS_ENUM = {
  success: 'success',
  running: 'running',
  failed: 'failed',
  unknown: 'unknown',
  unstarted: 'unstarted',
};

/**
 * 给表单的奇偶行设置 classname
 */
const setOddEvenClassName = (item) => {
  if (item._oddEvenIndex & 1) return 'even';
  return 'odd';
};

/**
 * 状态列自定义 render 组件
 */
const ColumnTaskComponent = ({ data }) => {
  const { taskStatus } = data;
  const statusMap = {
    [STATUS_ENUM.unknown]: '未知',
    [STATUS_ENUM.unstarted]: '未开始',
    [STATUS_ENUM.success]: '任务完成',
    [STATUS_ENUM.running]: '执行中',
    [STATUS_ENUM.failed]: '任务失败',
  };
  const className = ['task-status', STATUS_ENUM[taskStatus]].join(' ');
  return (<span className={className}>{ statusMap[taskStatus] }</span>);
};

/**
 * 操作列自定义 render 组件
 */
const ColumnOptionsComponent = ({ data }) => {
  let btnArr = [];
  let detailBtn = null;
  const status = data.taskStatus;
  const successNum = data.successNum || 0;
  const failedNum = data.failedNum || 0;
  const repeatNum = data.repeatNum || 0;

  const handleDownloadFailed = (event) => {
    event.preventDefault();
    RPCFactory.checkLogin
      .advertiserPageLogin()
      .done((result) => {
        if (result && result.success) {
          const url = `/api/v1/offline_task/download_fail_file?accountId=${window.pageProps.uid}&taskId=${data.taskId}`;
          window.location.href = url;
        } else if (judgeIsFromTMC()) {
          top.postMessage({ type: 'freshToken' }, '*');
        }
      })
      .fail((ret, msg) => {
        alert(msg);
      });
  };

  const downloadBtn = (
    <a key="download" onClick={handleDownloadFailed}>
      下载失败列表
    </a>
  );

  if (status === STATUS_ENUM.success) {
    detailBtn = (
      <OverlayTrigger
        trigger="hover"
        key="deltail-success"
        overlay={
          <PopOver>
            <div className="popover-message">
              <p>成功导入<span className="num">{ successNum }</span>条数据</p>
            </div>
          </PopOver>
        }
      >
        <a>详情</a>
      </OverlayTrigger>
    );
    btnArr = [detailBtn];
  } else if (status === STATUS_ENUM.failed) {
    detailBtn = (
      <OverlayTrigger
        trigger="hover"
        key="detail-failed"
        overlay={
          <PopOver>
            <div className="popover-message">
              <p>
                <span>成功导入</span>
                <span className="num">{ successNum }</span>
                <span>条线索，去重</span>
                <span className="num">{ repeatNum }</span>
                <span>条，</span>
                <span className="red">失败</span>
                <span className="num red">{ failedNum }</span>
                <span className="red">条，</span>
              </p>
              <p>请下载失败列表重新上传。</p>
            </div>
          </PopOver>
        }
      >
        <a>详情</a>
      </OverlayTrigger>
    );
    btnArr = [detailBtn, downloadBtn];
  } else {
    detailBtn =  (<a className="disabled" key="detail-running">详情</a>);
    btnArr = [detailBtn];
  }

  return (
    <div className="option-btns">
      { btnArr }
    </div>
  );
};

const CenterList = () => {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [list, setList] = useState([]);

  const getList = useCallback(() => {
    const fetchList = async () => {
      RPCFactory.leadsManage.getOfflineTasks({
        page,
        page_size: limit,
      }).done((res) => {
        // 拿到响应数据先设置页码数据
        setPage(get(res, 'page_info.page', 1));
        setTotal(get(res, 'page_info.total_number', 0));
        setLimit(get(res, 'page_info.page_size', 10));
        const rawList = get(res, 'entities') || [];
        const list = rawList.map((item = {}) => {
          const getStatus = (num) => {
            const statusMap = {
              0: STATUS_ENUM.unknown,
              1: STATUS_ENUM.unstarted,
              2: STATUS_ENUM.running,
              3: STATUS_ENUM.success,
              4: STATUS_ENUM.failed,
            };
            return statusMap[num];
          };
          const taskTypeMap = {
            1: '外部线索导入',
            2: '战败线索上传',
          };
          return {
            taskId: item.taskId,
            taskName: item.taskName,
            taskType: taskTypeMap[get(item, 'taskType')],
            taskStatus: getStatus(item.taskStatus),
            createTime: item.taskBeginTime,
            successNum: get(item, 'successNum', 0),
            failedNum: get(item, 'failNum', 0),
            repeatNum: get(item, 'duplicateNum', 0),
            failFileKey: item.failFileKey,
          };
        });
        setList(list);
      });
    };
    fetchList().catch(e => console.error('列表获取失败', e));
  }, [page, limit]);

  const handleLimitChange = (current, size) => {
    setLimit(size);
    setPage(1);
  };

  const handlePageChange = (_, current) => {
    setPage(current);
  };

  // 初始化页面加载数据
  useEffect(getList, [getList]);

  return (
    <main className="main" id="main">
      <div className="indipendent-page offline-task-center">
        <div className="page-header">
          <h3>离线任务中心</h3>
        </div>
        <div className="page-body">
          <div className="main-table">
            <Table data={list} setOddEvenClassName={setOddEvenClassName}>
              <Column name="taskName">任务名称</Column>
              <Column name="taskType">任务类型</Column>
              <Column name="taskStatus" customScope={['data']} component={ColumnTaskComponent}>
                任务状态
              </Column>
              <Column name="createTime">创建时间</Column>
              <Column name="options" customScope={['data']} component={ColumnOptionsComponent}>
                操作
              </Column>
            </Table>
            <PageBar
              value={page}
              pageSize={limit}
              totalSize={total}
              showTotalPage={false}
              pageSizeList={[10, 20, 30]}
              onChange={handlePageChange}
              onPageSizeChange={handleLimitChange}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default withNetCall(CenterList);
